/////////////////////////////////////////////////////////////////////////////////////////////////////////
//  컴포넌트명 : ChatWidget (플로팅 상담버튼 + 미니 상담패널)
//  설명 : mainFrame 우하단에 항상 떠있는 1:1 상담톡(X9003M1) 바로가기.
//        - 안읽음 개수를 가벼운 API(apiX9003M1Unread)로 30초마다 조회해 배지로 표시(패널이 열려있거나
//          화면이 안 보이면 조회하지 않음). 담당자 배지 = 승인대기 요청 수 + 상담중 안읽음 합계
//        - 일반 사용자(승인제) : 버튼 클릭 시 내 상담요청을 조회해서
//            . 진행중(승인대기/상담중) 요청이 있으면 그 방의 미니 채팅패널(ChatRoom 공용 컴포넌트)을 열고,
//            . 없으면 "새 상담 요청" 폼(ChatRequestForm 공용 컴포넌트)을 열음(지난 상담은 상담톡 탭에서 조회)
//        - 상담담당자 : 상담방 목록/승인(입장)이 필요하므로 패널 대신 X9003M1 탭을 열거나 활성화함
//        - 이미 X9003M1 탭이 열려있으면 패널을 또 띄우지 않고 그 탭으로 이동(읽음처리가 두 곳에서 겹치지
//          않게 함). 탭이 활성 상태일 땐 버튼 자체를 숨김.
//        - enabled=false(사용자 메뉴에 상담톡 메뉴가 없음)이면 아무것도 그리지 않음
//  개발이력 : 2026.09.20  최초개발 / 승인제(요청 폼/진행중 방) 반영
/////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config'; // API 기본 URL
import '../styles/chatDesk.css'; // 상담톡 전용 스타일(플로팅 버튼/패널 포함)
import ChatRoom, { ChatRoomInfo } from './ChatRoom';
import ChatRequestForm from './ChatRequestForm';

interface ChatWidgetProps {
  enabled: boolean;          // 상담톡 메뉴 권한 보유 여부(메뉴 트리에 LINK_KEY='CHAT' 메뉴가 있음)
  chatTabOpen: boolean;      // X9003M1 탭이 열려있음
  chatTabActive: boolean;    // X9003M1 탭이 현재 활성 탭
  onOpenTab: () => void;     // X9003M1 탭 열기/활성화
}

const UNREAD_POLL_MS = 30000;

// 상담 버튼을 끌어서 옮길 수 있음(2026-09-25) - 위치는 브라우저에 저장. 5px 이상 움직여야 드래그로 보고, 그때는 클릭(상담창 열기)을 무시함.
const FAB_SIZE = 52;
const FAB_POS_KEY = 'chat:fab:pos';
const FAB_DRAG_THRESHOLD = 5;
const clampFab = (x: number, y: number) => ({
  x: Math.max(0, Math.min(window.innerWidth - FAB_SIZE, x)),
  y: Math.max(0, Math.min(window.innerHeight - FAB_SIZE, y)),
});
const loadFabPos = (): { x: number; y: number } | null => {
  try {
    const v = JSON.parse(localStorage.getItem(FAB_POS_KEY) || 'null');
    return v && Number.isFinite(v.x) && Number.isFinite(v.y) ? clampFab(v.x, v.y) : null;
  } catch { return null; }
};

// 버튼 자체를 닫을 수 있음(2026-10-17) - x로 닫으면 새 상담요청/메시지가 와서 안읽음이 늘어나기 전까지는
// 다시 안 뜸(브라우저 저장소에 남아 새로고침해도 유지). "채팅요청이 오면 그때 나타나서 알림"이 핵심이라
// unread가 실제로 늘어나는 시점(0->1 등)에만 자동으로 다시 보여줌 - 이미 있던 안읽음이 남아있다고 바로
// 다시 뜨면 x가 무의미해지므로, 증가 트랜지션만 봄.
const FAB_DISMISS_KEY = 'chat:fab:dismissed';
const loadFabDismissed = (): boolean => {
  try { return localStorage.getItem(FAB_DISMISS_KEY) === '1'; } catch { return false; }
};
const saveFabDismissed = (v: boolean) => {
  try { v ? localStorage.setItem(FAB_DISMISS_KEY, '1') : localStorage.removeItem(FAB_DISMISS_KEY); } catch { /* 저장 실패 무시 */ }
};

type PanelView = { type: 'room'; room: ChatRoomInfo } | { type: 'form' };

const ChatWidget: React.FC<ChatWidgetProps> = ({ enabled, chatTabOpen, chatTabActive, onOpenTab }) => {
  const [unread, setUnread] = useState(0);
  const [isManager, setIsManager] = useState(false);
  const [open, setOpen] = useState(false);
  const [myId, setMyId] = useState('');
  const [view, setView] = useState<PanelView | null>(null);
  const [dismissed, setDismissed] = useState(loadFabDismissed);
  const prevUnreadRef = useRef(0);

  // 버튼 드래그 이동
  const [pos, setPos] = useState<{ x: number; y: number } | null>(loadFabPos);   // null = 기본 위치(CSS)
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef<{ sx: number; sy: number; ox: number; oy: number; moved: boolean } | null>(null);
  const suppressClickRef = useRef(false);
  useEffect(() => {   // 창 크기가 줄어도 버튼이 화면 밖으로 나가지 않게
    const onResize = () => setPos(p => (p ? clampFab(p.x, p.y) : p));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const onFabPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    const r = e.currentTarget.getBoundingClientRect();
    dragRef.current = { sx: e.clientX, sy: e.clientY, ox: r.left, oy: r.top, moved: false };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onFabPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.sx, dy = e.clientY - d.sy;
    if (!d.moved && Math.hypot(dx, dy) < FAB_DRAG_THRESHOLD) return;
    if (!d.moved) { d.moved = true; setDragging(true); }
    setPos(clampFab(d.ox + dx, d.oy + dy));
  };
  const onFabPointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    const d = dragRef.current;
    dragRef.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* 이미 해제됨 */ }
    if (d?.moved) {
      setDragging(false);
      suppressClickRef.current = true;                       // 드래그 끝에 따라오는 click은 상담창을 열지 않게 무시
      window.setTimeout(() => { suppressClickRef.current = false; }, 0);
      setPos(p => { if (p) { try { localStorage.setItem(FAB_POS_KEY, JSON.stringify(p)); } catch { /* 저장 실패 무시 */ } } return p; });
    }
  };
  // 버튼을 옮겼으면 미니 상담패널도 버튼 옆에 붙여서 펼침(버튼 아래 공간이 모자라면 위로). 모바일(전체화면 패널)은 CSS 그대로.
  const panelStyle = (): React.CSSProperties | undefined => {
    if (!pos || window.innerWidth <= 600) return undefined;
    const ph = Math.min(520, window.innerHeight - 40);
    const left = Math.max(8, Math.min(window.innerWidth - 360 - 8, pos.x + FAB_SIZE - 360));
    let top = pos.y + FAB_SIZE + 10;
    if (top + ph > window.innerHeight - 8) top = pos.y - ph - 10;
    return { left, top: Math.max(8, top), right: 'auto', height: ph };
  };

  const fetchUnread = useCallback(async () => {
    try {
      const res = await axios.post(`${API_BASE_URL}/za/apiX9003M1Unread`, {});
      const rs = res.data?.rsResults;
      if (rs) { setUnread(Number(rs.unread) || 0); setIsManager(!!rs.isManager); }
    } catch { /* 배지 조회 실패는 조용히 무시(다음 주기에 재시도) */ }
  }, []);

  // 안읽음 배지 폴링 - 버튼이 보이는 동안(탭 비활성 + 패널 닫힘 + 브라우저 화면 표시 중)에만 조회
  useEffect(() => {
    if (!enabled || open || chatTabActive) return;
    fetchUnread();
    const timer = window.setInterval(() => { if (document.visibilityState === 'visible') fetchUnread(); }, UNREAD_POLL_MS);
    return () => window.clearInterval(timer);
  }, [enabled, open, chatTabActive, fetchUnread]);

  // 상담톡 탭이 활성화되면(직접 열었거나 버튼으로 이동) 미니패널은 닫음
  useEffect(() => { if (chatTabActive) setOpen(false); }, [chatTabActive]);

  // 닫아둔 상태에서 안읽음이 실제로 늘어나면(새 상담요청/메시지 도착) 버튼을 다시 표시
  useEffect(() => {
    if (dismissed && unread > prevUnreadRef.current) { setDismissed(false); saveFabDismissed(false); }
    prevUnreadRef.current = unread;
  }, [unread, dismissed]);

  const onDismiss = useCallback((e: React.PointerEvent | React.MouseEvent) => {
    e.stopPropagation();
    setDismissed(true);
    saveFabDismissed(true);
  }, []);

  // 내 상담요청 조회 후 진행중(승인대기/상담중) 방이 있으면 그 방, 없으면 요청 폼을 패널에 표시
  const loadPanel = useCallback(async (preferRoomId?: number) => {
    const res = await axios.post(`${API_BASE_URL}/za/apiX9003M1MyRooms`, {});
    const list: any[] = Array.isArray(res.data?.rsResults) ? res.data.rsResults : [];
    const norm = (r: any): ChatRoomInfo => ({ ...r, room_id: Number(r.room_id), user_unread: Number(r.user_unread) || 0 });
    const target = (preferRoomId ? list.find(r => Number(r.room_id) === preferRoomId) : undefined) ?? list.find(r => r.status === 'W' || r.status === 'A');
    setView(target ? { type: 'room', room: norm(target) } : { type: 'form' });
  }, []);

  const onFabClick = useCallback(async () => {
    if (isManager || chatTabOpen) { setOpen(false); onOpenTab(); return; }
    if (open) { setOpen(false); return; }
    try {
      const init = await axios.post(`${API_BASE_URL}/za/apiX9003M1Init`, {});
      const rs = init.data?.rsResults;
      if (rs?.isManager) { setIsManager(true); onOpenTab(); return; }
      setMyId(rs?.userId || '');
      await loadPanel();
      setUnread(0);
      setOpen(true);
    } catch { /* 진입 실패 시 아무 동작 안 함(메뉴의 상담톡 화면에서 오류 안내가 표시됨) */ }
  }, [isManager, chatTabOpen, open, onOpenTab, loadPanel]);

  const onRead = useCallback(() => setUnread(0), []);
  const onClose = useCallback(() => setOpen(false), []);
  const onCreated = useCallback((roomId: number) => { loadPanel(roomId).catch(() => {}); }, [loadPanel]);
  const onNewRequest = useCallback(() => setView({ type: 'form' }), []);
  // 상담이 종료(C)되면 미니패널 채팅창을 리셋 - 진행중 방이 없으니 새 상담 요청 폼으로 돌아감(이력은 상담톡 탭에서 조회)
  const onRoomChange = useCallback((st: { status: string }) => { if (st.status === 'C') loadPanel().catch(() => {}); }, [loadPanel]);

  if (!enabled || chatTabActive || dismissed) return null;

  return (
    <>
      {open && view && (
        <div className="chat-panel" style={panelStyle()}>
          {view.type === 'room' ? (
            <ChatRoom
              key={view.room.room_id}
              room={view.room}
              myId={myId}
              isManager={false}
              onClose={onClose}
              onRead={onRead}
              onRoomChange={onRoomChange}
              onNewRequest={onNewRequest}
            />
          ) : (
            <div className="chat-panel-form">
              <button type="button" className="chatDesk-close chat-panel-x" onClick={onClose} title="닫기"><i className="fas fa-times" /></button>
              <ChatRequestForm compact onCreated={onCreated} onCancel={onClose} />
              <button type="button" className="chatDesk-link" onClick={() => { setOpen(false); onOpenTab(); }}>지난 상담 보기</button>
            </div>
          )}
        </div>
      )}
      <button type="button" className={`chat-fab${dragging ? ' dragging' : ''}`} title="1:1 상담 (끌어서 위치 이동)"
        style={pos ? { left: pos.x, top: pos.y, right: 'auto', bottom: 'auto' } : undefined}
        onPointerDown={onFabPointerDown} onPointerMove={onFabPointerMove} onPointerUp={onFabPointerUp} onPointerCancel={onFabPointerUp}
        onClick={() => { if (suppressClickRef.current) return; onFabClick(); }}>
        <i className={`fas ${open ? 'fa-chevron-down' : 'fa-comments'}`} />
        {!open && unread > 0 && <span className="chatDesk-badge chat-fab-badge">{unread > 99 ? '99+' : unread}</span>}
        <span className="chat-fab-dismiss" title="닫기(새 상담요청/메시지가 오면 다시 표시됩니다)"
          onPointerDown={e => e.stopPropagation()} onClick={onDismiss}>
          <i className="fas fa-xmark" />
        </span>
      </button>
    </>
  );
};

export default ChatWidget;
